import { useState } from 'react';
import {
  confirmStagedMatch,
  confirmSelectiveMerge,
  createStagedAsNew,
  rejectStaged,
} from '../services/StagingRepository';
import {
  FIELD_NAME_TO_KEY,
  SHIPMENT_FIELD_NAME_TO_KEY,
} from '../utils/stagingConstants';

/**
 * Manages confirm/create/reject actions for the staging review modal.
 */
const useStagingActions = ({ stagedVoyage, onResolved, onClose }) => {
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleConfirmMatch = async ({
    selectedCandidate,
    mergeMode,
    hasAnySelection,
    fieldSelections,
    shipmentSelections,
    shipmentFieldSelections,
  }) => {
    if (!selectedCandidate) return;
    if (mergeMode === 'selective' && !hasAnySelection) {
      setSnackbar({
        open: true,
        message: 'Please make at least one selection before confirming',
        severity: 'warning',
      });
      return;
    }
    setActionLoading(true);
    try {
      if (mergeMode === 'selective') {
        const selectedVoyageFields = Object.entries(fieldSelections)
          .filter(([, v]) => v === 'api')
          .map(([fieldName]) => FIELD_NAME_TO_KEY[fieldName])
          .filter(Boolean);

        const shipmentInstructions = Object.entries(shipmentSelections).map(
          ([marketId, action]) => {
            const instr = { marketId: parseInt(marketId), action };
            const perField = shipmentFieldSelections[parseInt(marketId)];
            if (action === 'TAKE_API' && perField) {
              const selectedFields = Object.entries(perField)
                .filter(([, v]) => v === 'api')
                .map(([fn]) => SHIPMENT_FIELD_NAME_TO_KEY[fn])
                .filter(Boolean);
              if (selectedFields.length > 0) {
                instr.selectedFields = selectedFields;
              }
            }
            return instr;
          }
        );

        await confirmSelectiveMerge(
          stagedVoyage.id,
          selectedCandidate.voyageId,
          {
            takeAllFromApi: false,
            selectedVoyageFields,
            shipmentInstructions,
          }
        );
      } else {
        await confirmStagedMatch(stagedVoyage.id, selectedCandidate.voyageId);
      }
      setSnackbar({
        open: true,
        message: 'Match confirmed successfully',
        severity: 'success',
      });
      onResolved();
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to confirm match',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAsNew = async () => {
    setActionLoading(true);
    try {
      await createStagedAsNew(stagedVoyage.id);
      setSnackbar({
        open: true,
        message: 'Voyage created successfully',
        severity: 'success',
      });
      onResolved();
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create voyage',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await rejectStaged(stagedVoyage.id);
      setSnackbar({
        open: true,
        message: 'Staged voyage rejected',
        severity: 'info',
      });
      onResolved();
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to reject',
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  return {
    actionLoading,
    snackbar,
    closeSnackbar,
    handleConfirmMatch,
    handleCreateAsNew,
    handleReject,
  };
};

export default useStagingActions;
